import axios from 'axios';
import { getActiveAuthToken, getActiveSessionRevision, getActiveSessionRoles,
  getActiveSessionType, SESSION_TYPE_CLOUD } from './authenticatedClient';

const owners = new WeakSet();
const writes = new Map();
const rolesKey = roles => JSON.stringify([...new Set((Array.isArray(roles) ? roles : [])
  .filter(role => typeof role === 'string').map(role => role.trim().toLowerCase()))].sort());
const validLocal = value => {
  try { const u = new URL(value); return u.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(u.hostname)
    && !u.username && !u.password && u.pathname === '/' && !u.search && !u.hash; } catch { return false; }
};
const validCloud = value => {
  try { const u = new URL(value); return ['https:', 'http:'].includes(u.protocol)
    && !u.username && !u.password && u.pathname === '/' && !u.search && !u.hash; } catch { return false; }
};
export const retiredSyncResult = () => ({ state: 'skipped', reason: 'context_changed' });

// TCA-26101917: renderer-lifetime ownership, not native authority or durable receipts.
export function captureDesktopSyncOwner(context, installationOnly = false) {
  if (owners.has(context?.owner)) return context.owner;
  const bridge = typeof window === 'undefined' ? null : window.lvbDesktop;
  const revision = installationOnly ? null : getActiveSessionRevision();
  const token = installationOnly ? null : getActiveAuthToken();
  const roleKey = installationOnly ? null : rolesKey(getActiveSessionRoles());
  const cloud = bridge?.backendUrl;
  const local = bridge?.localBackendUrl;
  const admitted = !!bridge && !!token && getActiveSessionType() === SESSION_TYPE_CLOUD
    && validLocal(local) && validCloud(cloud) && cloud !== local
    && (context?.sessionRevision === undefined || context.sessionRevision === revision)
    && (context?.roles === undefined || rolesKey(context.roles) === roleKey);
  let active = true;
  const owner = { bridge, revision, token, roleKey, cloud, local, authenticated: !installationOnly,
    userId: context?.userId || null, tenant: context?.tenant || null,
    config: { headers: token ? { Authorization: `Bearer ${token}` } : {}, suppressServerErrorNotification: true },
    current: () => active && !!bridge && validLocal(local) && window.lvbDesktop === bridge
      && bridge.backendUrl === cloud && bridge.localBackendUrl === local
      && (installationOnly || (admitted && getActiveSessionRevision() === revision && getActiveAuthToken() === token
        && getActiveSessionType() === SESSION_TYPE_CLOUD && rolesKey(getActiveSessionRoles()) === roleKey)),
    retire: () => { active = false; },
  };
  owners.add(owner);
  return owner;
}
export const captureDesktopObservationOwner = () => captureDesktopSyncOwner(undefined, true);

export const sameSyncOwner = (a, b) => a.current() && b.current()
  && ['bridge', 'revision', 'token', 'roleKey', 'cloud', 'local', 'userId', 'tenant', 'authenticated'].every(key => a[key] === b[key]);

export async function ownedSyncRead(owner, execute) {
  if (!owner.current()) throw new Error('context_changed');
  const controller = new AbortController();
  let timer;
  try {
    const result = await Promise.race([
      Promise.resolve().then(() => {
        if (!owner.current() || controller.signal.aborted) throw new Error('context_changed');
        return execute({ ...owner.config, signal: controller.signal, timeout: 30000 });
      }),
      new Promise((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(new Error('read_unavailable')); }, 30000); }),
    ]);
    if (!owner.current()) throw new Error('context_changed');
    return result;
  } finally { clearTimeout(timer); }
}

export async function captureCacheBinding(owner) {
  const response = await ownedSyncRead(owner, config => axios.get(`${owner.local}/api/public/shop-branding`, config));
  if (!response?.data || typeof response.data !== 'object' || Array.isArray(response.data)) throw new Error('cache_observation_unavailable');
  const binding = response.headers?.get?.('X-LVB-Cache-Binding') ?? response.headers?.['x-lvb-cache-binding'] ?? null;
  if (binding !== null && (typeof binding !== 'string' || !/^[a-f0-9]{64}$/.test(binding))) throw new Error('invalid_cache_binding');
  return binding;
}

export function ownedCacheWrite(owner, resource, payload, binding, validate, accepted) {
  if (!owner.authenticated || !owner.current()) return Promise.resolve(retiredSyncResult());
  // Loopback aliases at the same port may address the same local server.
  const key = `${new URL(owner.local).port || '80'}/${resource}`;
  if (writes.has(key)) return Promise.resolve({ state: 'pending', reason: 'cache_write_pending' });
  const claim = {};
  writes.set(key, claim);
  const release = () => { if (writes.get(key) === claim) writes.delete(key); };
  const uncertain = () => ({ state: 'uncertain', reason: 'cache_write_outcome_unknown' });
  let timer;
  const operation = Promise.resolve().then(() => {
    if (!owner.current()) { release(); return retiredSyncResult(); }
    // Deliberately no read-style abort/deadline on a dispatched mutation.
    return axios.post(`${owner.local}/api/desktop/cache/${resource}`, payload, {
      ...owner.config, headers: { ...owner.config.headers, ...(binding ? { 'X-LVB-Cache-Binding': binding } : {}) },
    }).then(response => {
      const returnedBinding = response?.headers?.get?.('X-LVB-Cache-Binding') ?? response?.headers?.['x-lvb-cache-binding'] ?? null;
      if ((returnedBinding !== binding) || !validate(response?.data)) return uncertain();
      release();
      if (owner.current()) { try { accepted?.(); } catch { /* Optional tracking cannot undo acknowledged storage. */ } }
      return { state: 'cached', ...(resource === 'users' ? { count: payload.length } : {}) };
    }).catch(error => {
      if (error?.response?.status === 409 && error.response.data?.detail?.code === 'cache_operation_changed') {
        release(); return { state: 'skipped', reason: 'cache_operation_changed' };
      }
      // A lost reply or malformed acknowledgement is not permission to overlap another write.
      return uncertain();
    });
  }).catch(uncertain);
  return Promise.race([operation, new Promise(resolve => { timer = setTimeout(() => resolve(uncertain()), 30000); })])
    .finally(() => clearTimeout(timer));
}

export function resetDesktopSyncWritesForTests() { writes.clear(); }
